import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import AppHeader from "../components/AppHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "../components/ui/dialog";
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from "../components/ui/tabs";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
    ArrowLeft,
    Calendar,
    Clock,
    Plus,
    ExternalLink,
    Trophy,
    RefreshCcw,
    Edit3,
    Eye,
    GitMerge,
    Trash2,
    Flag,
    Copy,
    Eye as EyeIcon,
    EyeOff,
    Handshake,
    Award,
    Pencil,
    Image as ImageIcon,
    BookOpen,
    Plus as PlusIcon,
    Users,
} from "lucide-react";
import TimelineEditor from "../components/TimelineEditor";
import SponsorsEditor from "../components/SponsorsEditor";
import PlayoffSelector from "../components/PlayoffSelector";
import AddTeamDialog from "../components/AddTeamDialog";
import AnalyticsPanel from "../components/AnalyticsPanel";
import {
    ResultDialog,
    ReagendarDialog,
    JornadaDialog,
} from "../components/MatchDialogs";

export default function TournamentDetail() {
    const { tid } = useParams();
    const nav = useNavigate();
    const [data, setData] = useState(null);
    const [tab, setTab] = useState("organizar");
    const [activeJornada, setActiveJornada] = useState(null);
    const [resultMatch, setResultMatch] = useState(null);
    const [reagendarMatch, setReagendarMatch] = useState(null);
    const [jornadaOpen, setJornadaOpen] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get(`/tournaments/${tid}`);
            setData(data);
        } catch (e) {
            toast.error("No se pudo cargar el torneo");
            nav("/dashboard");
        }
    }, [tid, nav]);

    useEffect(() => {
        load();
    }, [load]);

    if (!data) {
        return (
            <div className="min-h-screen bg-slate-50">
                <AppHeader />
                <div className="p-12 text-center text-slate-500">Cargando…</div>
            </div>
        );
    }

    const { tournament, matches } = data;
    const isLeague = tournament.kind === "league";

    const handleMove = async (matchId, { hora, cancha, dia }) => {
        try {
            const payload = { hora, cancha };
            if (dia !== undefined) payload.dia = dia;
            await api.patch(`/matches/${matchId}`, payload);
            // Optimistic local update
            setData((prev) => ({
                ...prev,
                matches: prev.matches.map((m) =>
                    m.id === matchId
                        ? { ...m, hora, cancha, ...(dia !== undefined ? { dia } : {}) }
                        : m,
                ),
            }));
        } catch (err) {
            toast.error(
                formatApiError(err.response?.data?.detail) ||
                    "No se pudo mover el partido",
            );
            load();
        }
    };

    const handleSwap = async (matchId, otherId) => {
        try {
            await api.post(`/matches/${matchId}/swap`, { other_id: otherId });
            await load();
        } catch (err) {
            toast.error(
                formatApiError(err.response?.data?.detail) ||
                    "No se pudo intercambiar el partido",
            );
            load();
        }
    };

    const handleRelease = async (match) => {
        if (
            !window.confirm(
                `Vas a liberar este slot. "${match.local} vs ${match.visitante}" se envía a la bolsa y el horario queda vacío para un evento intermedio. ¿Continuar?`,
            )
        )
            return;
        try {
            await api.post(`/matches/${match.id}/release`);
            toast.success("Espacio liberado. El partido está en la bolsa.");
            await load();
        } catch (err) {
            toast.error(
                formatApiError(err.response?.data?.detail) ||
                    "No se pudo liberar el espacio",
            );
            load();
        }
    };

    const handleFillSlot = async (matchId, { hora, cancha, dia, jornada_id }) => {
        try {
            const payload = { hora, cancha, status: "scheduled" };
            if (dia !== undefined) payload.dia = dia;
            if (jornada_id !== undefined) payload.jornada_id = jornada_id;
            await api.patch(`/matches/${matchId}`, payload);
            toast.success("Partido añadido al slot.");
            await load();
        } catch (err) {
            toast.error(
                formatApiError(err.response?.data?.detail) ||
                    "No se pudo añadir el partido al slot",
            );
        }
    };

    return (
        <div className="min-h-screen bg-slate-50">
            <AppHeader />
            <main className="max-w-7xl mx-auto px-6 py-8">
                <button
                    onClick={() => nav("/dashboard")}
                    className="text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 text-sm mb-6"
                    data-testid="back-dashboard"
                >
                    <ArrowLeft className="w-4 h-4" /> Panel
                </button>

                <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                        <span
                            className={`text-xs font-bold uppercase tracking-widest px-2 py-1 rounded-full ${
                                isLeague
                                    ? "bg-ipn-100 text-ipn-900"
                                    : "bg-yellow-100 text-yellow-800"
                            }`}
                        >
                            {isLeague ? "Liga" : "Torneo Rápido"}
                        </span>
                        <h1
                            className="font-display text-3xl lg:text-4xl font-black mt-3"
                            data-testid="tournament-title"
                        >
                            {tournament.name}
                        </h1>
                        <div className="mt-2 text-slate-500 text-sm flex flex-wrap gap-4">
                            <span>{tournament.canchas} canchas</span>
                            <span>·</span>
                            <span>
                                {tournament.categorias?.length || 0} categorías
                            </span>
                            <span>·</span>
                            <span>{tournament.tipo_voley}</span>
                        </div>
                    </div>

                    <div className="flex gap-2 flex-wrap">
                        <RefereePinCard tournamentId={tournament.id} tournament={tournament} />
                        <Link
                            to={`/public/${tournament.slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 h-11 px-4 rounded-xl bg-white border border-slate-200 hover:bg-slate-50 font-semibold text-sm"
                            data-testid="open-public"
                        >
                            <ExternalLink className="w-4 h-4" />
                            Vista pública
                        </Link>
                    </div>
                </div>

                <Tabs value={tab} onValueChange={setTab} className="mt-8">
                    <TabsList className="bg-white border border-slate-100 rounded-xl p-1 h-12">
                        <TabsTrigger
                            value="organizar"
                            className="rounded-lg h-10 px-4 data-[state=active]:bg-slate-900 data-[state=active]:text-white"
                            data-testid="tab-organizar"
                        >
                            <Calendar className="w-4 h-4 mr-2" />
                            Organizar
                        </TabsTrigger>
                        <TabsTrigger
                            value="edicion"
                            className="rounded-lg h-10 px-4 data-[state=active]:bg-slate-900 data-[state=active]:text-white"
                            data-testid="tab-edicion"
                        >
                            <Edit3 className="w-4 h-4 mr-2" />
                            Edición
                        </TabsTrigger>
                        {isLeague && (
                            <TabsTrigger
                                value="reagendar"
                                className="rounded-lg h-10 px-4 data-[state=active]:bg-slate-900 data-[state=active]:text-white"
                                data-testid="tab-reagendar"
                            >
                                <GitMerge className="w-4 h-4 mr-2" />
                                Reagendar
                            </TabsTrigger>
                        )}
                        {isLeague && (
                            <TabsTrigger
                                value="playoffs"
                                className="rounded-lg h-10 px-4 data-[state=active]:bg-slate-900 data-[state=active]:text-white"
                                data-testid="tab-playoffs"
                            >
                                <Award className="w-4 h-4 mr-2" />
                                Playoffs
                            </TabsTrigger>
                        )}
                        <TabsTrigger
                            value="patrocinios"
                            className="rounded-lg h-10 px-4 data-[state=active]:bg-slate-900 data-[state=active]:text-white"
                            data-testid="tab-patrocinios"
                        >
                            <Handshake className="w-4 h-4 mr-2" />
                            Patrocinios
                        </TabsTrigger>
                        <TabsTrigger
                            value="reglamento"
                            className="rounded-lg h-10 px-4 data-[state=active]:bg-slate-900 data-[state=active]:text-white"
                            data-testid="tab-reglamento"
                        >
                            <BookOpen className="w-4 h-4 mr-2" />
                            Reglamento
                        </TabsTrigger>
                        <TabsTrigger
                            value="vista"
                            className="rounded-lg h-10 px-4 data-[state=active]:bg-slate-900 data-[state=active]:text-white"
                            data-testid="tab-vista"
                        >
                            <Eye className="w-4 h-4 mr-2" />
                            Vista previa
                        </TabsTrigger>
                    </TabsList>

                    {/* ORGANIZAR */}
                    <TabsContent value="organizar" className="mt-6">
                        <OrganizarTab
                            tournament={tournament}
                            matches={matches}
                            isLeague={isLeague}
                            activeJornada={activeJornada}
                            setActiveJornada={setActiveJornada}
                            setResultMatch={setResultMatch}
                            onCreateJornada={() => setJornadaOpen(true)}
                            onDeleteJornada={async (jid) => {
                                if (!window.confirm("¿Eliminar jornada?"))
                                    return;
                                try {
                                    await api.delete(
                                        `/tournaments/${tournament.id}/jornadas/${jid}`,
                                    );
                                    toast.success("Jornada eliminada");
                                    load();
                                } catch (err) {
                                    toast.error(
                                        formatApiError(err.response?.data?.detail),
                                    );
                                }
                            }}
                            onReload={load}
                        />
                    </TabsContent>

                    {/* EDICION */}
                    <TabsContent value="edicion" className="mt-6">
                        <EdicionTab
                            tournament={tournament}
                            matches={matches}
                            isLeague={isLeague}
                            activeJornada={activeJornada}
                            setActiveJornada={setActiveJornada}
                            onMove={handleMove}
                            onSwap={handleSwap}
                            onRelease={handleRelease}
                            onFillSlot={handleFillSlot}
                            onRecalcular={async (n) => {
                                try {
                                    await api.post(
                                        `/tournaments/${tournament.id}/recalcular`,
                                        { nuevas_canchas: n },
                                    );
                                    toast.success(
                                        "Rol recalculado (solo partidos no jugados)",
                                    );
                                    load();
                                } catch (err) {
                                    toast.error(
                                        formatApiError(err.response?.data?.detail),
                                    );
                                }
                            }}
                        />
                    </TabsContent>

                    {/* REAGENDAR */}
                    {isLeague && (
                        <TabsContent value="reagendar" className="mt-6">
                            <ReagendarTab
                                tournament={tournament}
                                matches={matches}
                                onReagendar={(m) => setReagendarMatch(m)}
                            />
                        </TabsContent>
                    )}

                    {/* VISTA PREVIA */}
                    <TabsContent value="vista" className="mt-6">
                        <VistaPreviaTab
                            tournament={tournament}
                            matches={matches}
                            onReload={load}
                        />
                    </TabsContent>

                    {/* PATROCINIOS */}
                    <TabsContent value="patrocinios" className="mt-6">
                        <div className="space-y-6">
                            <AnalyticsPanel tournament={tournament} />
                            <SponsorsEditor tournamentId={tournament.id} />
                        </div>
                    </TabsContent>

                    {/* REGLAMENTO */}
                    <TabsContent value="reglamento" className="mt-6">
                        <ReglamentoEditor
                            tournament={tournament}
                            onSaved={load}
                        />
                    </TabsContent>

                    {/* PLAYOFFS (solo Liga) */}
                    {isLeague && (
                        <TabsContent value="playoffs" className="mt-6">
                            <PlayoffSelector
                                tournament={tournament}
                                matches={matches}
                                onSaved={load}
                            />
                        </TabsContent>
                    )}
                </Tabs>

                {resultMatch && (
                    <ResultDialog
                        match={resultMatch}
                        open={!!resultMatch}
                        onOpenChange={(o) => !o && setResultMatch(null)}
                        onSaved={load}
                    />
                )}
                {reagendarMatch && (
                    <ReagendarDialog
                        match={reagendarMatch}
                        jornadas={tournament.jornadas || []}
                        open={!!reagendarMatch}
                        onOpenChange={(o) => !o && setReagendarMatch(null)}
                        onSaved={load}
                    />
                )}
                {jornadaOpen && (
                    <JornadaDialog
                        tournament={tournament}
                        open={jornadaOpen}
                        onOpenChange={setJornadaOpen}
                        onSaved={load}
                    />
                )}
            </main>
        </div>
    );
}

function OrganizarTab({
    tournament,
    matches,
    isLeague,
    activeJornada,
    setActiveJornada,
    setResultMatch,
    onCreateJornada,
    onDeleteJornada,
    onReload,
}) {
    const jornadas = tournament.jornadas || [];
    const [friendlyOpen, setFriendlyOpen] = useState(false);

    const friendlyButton = (
        <Button
            onClick={() => setFriendlyOpen(true)}
            variant="outline"
            className="h-11 rounded-xl font-bold border-amber-300 text-amber-800 hover:bg-amber-50"
            data-testid="add-friendly-match"
        >
            <Trophy className="w-4 h-4 mr-1" />
            Amistoso
        </Button>
    );

    const friendlyDialog = friendlyOpen && (
        <FriendlyMatchDialog
            tournament={tournament}
            open={friendlyOpen}
            onOpenChange={setFriendlyOpen}
            onSaved={onReload}
        />
    );

    if (isLeague) {
        return (
            <div className="space-y-6">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div>
                        <h2 className="font-display text-xl font-bold">
                            Jornadas
                        </h2>
                        <p className="text-sm text-slate-500">
                            Programa una nueva jornada eligiendo qué rama
                            juega.
                        </p>
                    </div>
                    <div className="flex gap-2">
                        {friendlyButton}
                        <Button
                            onClick={onCreateJornada}
                            className="h-11 rounded-xl bg-ipn-800 hover:bg-ipn-900 font-bold"
                            data-testid="add-jornada"
                        >
                            <Plus className="w-4 h-4 mr-1" />
                            Nueva jornada
                        </Button>
                    </div>
                </div>

                {jornadas.length === 0 && (
                    <div className="vp-card p-10 text-center">
                        <Calendar className="w-10 h-10 mx-auto text-ipn-800" />
                        <h3 className="mt-4 font-display text-xl font-bold">
                            Aún no programas ninguna jornada
                        </h3>
                        <p className="mt-2 text-slate-500 text-sm">
                            Tienes una bolsa de partidos en borrador esperando
                            ser programados.
                        </p>
                    </div>
                )}

                <div className="grid gap-4">
                    {jornadas.map((j) => {
                        const jm = matches
                            .filter((m) => m.jornada_id === j.id)
                            .slice()
                            .sort((a, b) => {
                                const ha = a.hora || "99:99";
                                const hb = b.hora || "99:99";
                                if (ha !== hb) return ha.localeCompare(hb);
                                return (a.cancha || 0) - (b.cancha || 0);
                            });
                        return (
                            <div key={j.id} className="vp-card p-6">
                                <div className="flex items-start justify-between gap-4 flex-wrap">
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <span
                                                className={`text-xs font-bold uppercase px-2 py-1 rounded-full ${
                                                    j.modo === "mixto"
                                                        ? "bg-purple-100 text-purple-700"
                                                        : j.modo === "femenil"
                                                          ? "bg-rose-100 text-rose-700"
                                                          : "bg-ipn-100 text-ipn-900"
                                                }`}
                                            >
                                                {j.modo}
                                            </span>
                                            <h3 className="font-display font-bold text-lg">
                                                {j.fecha}
                                            </h3>
                                        </div>
                                        <p className="text-sm text-slate-500 mt-1">
                                            {jm.length} partidos · {j.hora_inicio}
                                            –{j.hora_fin}
                                        </p>
                                    </div>
                                    <div className="flex gap-2">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => setActiveJornada(j.id)}
                                            className="rounded-xl"
                                            data-testid={`jornada-edit-${j.id}`}
                                        >
                                            <Edit3 className="w-3 h-3 mr-1" />
                                            Editar
                                        </Button>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => onDeleteJornada(j.id)}
                                            className="rounded-xl text-rose-600 hover:bg-rose-50 hover:text-rose-700 border-rose-100"
                                            data-testid={`jornada-delete-${j.id}`}
                                        >
                                            <Trash2 className="w-3 h-3" />
                                        </Button>
                                    </div>
                                </div>

                                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {jm.map((m) => (
                                        <MatchRow
                                            key={m.id}
                                            match={m}
                                            onClick={() => setResultMatch(m)}
                                        />
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Drafts */}
                <div className="vp-card p-6">
                    <h3 className="font-display text-lg font-bold mb-3">
                        Bolsa de partidos pendientes
                    </h3>
                    <DraftBag matches={matches} />
                </div>
                {friendlyDialog}
            </div>
        );
    }

    // Quick tournament: agrupar por categoría
    return (
        <>
            <div className="flex justify-end mb-3">{friendlyButton}</div>
            <QuickOrganizar
                tournament={tournament}
                matches={matches}
                setResultMatch={setResultMatch}
            />
            {friendlyDialog}
        </>
    );
}

function QuickOrganizar({ tournament, matches, setResultMatch }) {
    return (
        <div className="space-y-6">
            {tournament.categorias.map((cat) => {
                const cm = matches
                    .filter((m) => m.category_id === cat.id)
                    .slice()
                    .sort((a, b) => {
                        const da = a.dia || 0;
                        const db = b.dia || 0;
                        if (da !== db) return da - db;
                        const ha = a.hora || "99:99";
                        const hb = b.hora || "99:99";
                        if (ha !== hb) return ha.localeCompare(hb);
                        return (a.cancha || 0) - (b.cancha || 0);
                    });
                return (
                    <div key={cat.id} className="vp-card p-6">
                        <div className="flex items-center gap-2">
                            <span
                                className={`text-xs font-bold uppercase px-2 py-1 rounded-full ${
                                    cat.rama === "femenil"
                                        ? "bg-rose-100 text-rose-700"
                                        : "bg-ipn-100 text-ipn-900"
                                }`}
                            >
                                {cat.rama}
                            </span>
                            <h3 className="font-display font-bold text-lg">
                                {cat.nombre}
                            </h3>
                            <span className="text-xs text-slate-500 ml-auto">
                                {cm.length} partidos
                            </span>
                        </div>
                        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                            {cm.map((m) => (
                                <MatchRow
                                    key={m.id}
                                    match={m}
                                    onClick={() => setResultMatch(m)}
                                />
                            ))}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

function MatchRow({ match, onClick }) {
    const played = match.status === "played";
    const friendly = match.is_friendly === true;
    return (
        <button
            onClick={onClick}
            className={`text-left p-4 rounded-xl border transition-all ${
                played
                    ? "bg-emerald-50 border-emerald-200"
                    : friendly
                      ? "bg-amber-50 border-amber-200 hover:border-amber-400"
                      : "bg-white border-slate-200 hover:border-ipn-300 hover:shadow-sm"
            }`}
            data-testid={`match-row-${match.id}`}
        >
            <div className="flex items-center justify-between text-xs font-bold uppercase tracking-widest text-slate-400">
                <span>
                    {match.dia ? `Día ${match.dia} · ` : ""}
                    {match.hora || "—"} · Cancha {match.cancha}
                </span>
                <span className="flex items-center gap-1">
                    {friendly && (
                        <span
                            className="px-1.5 py-0.5 rounded text-[9px] font-black bg-amber-500 text-white tracking-widest"
                            data-testid={`friendly-badge-${match.id}`}
                        >
                            AMISTOSO
                        </span>
                    )}
                    {match.fase}
                </span>
            </div>
            <div className="mt-2 font-semibold flex items-center justify-between gap-2">
                <span className="truncate">{match.local}</span>
                <span className="text-slate-400">vs</span>
                <span className="truncate text-right">{match.visitante}</span>
            </div>
            <div className="mt-2 text-xs text-slate-500 flex items-center justify-between">
                <span
                    className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                        match.branch === "femenil"
                            ? "bg-rose-100 text-rose-700"
                            : "bg-ipn-100 text-ipn-900"
                    }`}
                >
                    {match.branch}
                </span>
                <span className="font-bold">
                    {played ? `Final: ${match.score}` : "Pendiente"}
                </span>
            </div>
        </button>
    );
}

function DraftBag({ matches }) {
    const drafts = matches.filter((m) => m.status === "draft");
    if (drafts.length === 0)
        return (
            <p className="text-sm text-slate-500">
                No hay partidos en borrador. Todos están programados.
            </p>
        );
    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            {drafts.slice(0, 60).map((m) => (
                <div
                    key={m.id}
                    className={`p-3 rounded-xl border ${
                        m.branch === "femenil"
                            ? "bg-rose-50 border-rose-200"
                            : "bg-ipn-50 border-ipn-200"
                    }`}
                    data-testid={`draft-${m.id}`}
                >
                    <div className="text-[10px] uppercase font-bold opacity-60">
                        {m.branch} · {m.fase}
                    </div>
                    <div className="mt-1 text-sm font-semibold">
                        {m.local} vs {m.visitante}
                    </div>
                </div>
            ))}
            {drafts.length > 60 && (
                <div className="text-xs text-slate-500 col-span-full">
                    + {drafts.length - 60} más…
                </div>
            )}
        </div>
    );
}

function EdicionTab({
    tournament,
    matches,
    isLeague,
    activeJornada,
    setActiveJornada,
    onMove,
    onSwap,
    onRelease,
    onFillSlot,
    onRecalcular,
}) {
    const [nuevasCanchas, setNuevasCanchas] = useState(tournament.canchas);

    // For league: pick a jornada; for quick: show all
    const jornadas = tournament.jornadas || [];
    const selJornada =
        isLeague && jornadas.length > 0
            ? jornadas.find((j) => j.id === activeJornada) || jornadas[0]
            : null;

    // Días disponibles (torneos rápidos con dias_cfg o varios "dia" en los partidos)
    const availableDays = useMemo(() => {
        if (isLeague) return [];
        const set = new Set();
        matches.forEach((m) => {
            if (m.dia) set.add(m.dia);
        });
        // Complementa con la longitud de dias_cfg configurada
        const cfg = tournament.dias_cfg || [];
        for (let i = 1; i <= cfg.length; i++) set.add(i);
        const arr = Array.from(set).sort((a, b) => a - b);
        return arr.length ? arr : [1];
    }, [matches, tournament.dias_cfg, isLeague]);

    const [selectedDay, setSelectedDay] = useState(availableDays[0] || 1);
    useEffect(() => {
        if (!availableDays.includes(selectedDay)) {
            setSelectedDay(availableDays[0] || 1);
        }
    }, [availableDays, selectedDay]);

    // Bolsa: partidos en draft del torneo. Para Liga filtramos por modo de la jornada
    // para evitar mezclar ramas que no aplican.
    const draftMatches = useMemo(() => {
        const all = matches.filter((m) => m.status === "draft");
        if (isLeague && selJornada) {
            const modo = selJornada.modo;
            if (modo === "varonil") return all.filter((d) => d.branch === "varonil");
            if (modo === "femenil") return all.filter((d) => d.branch === "femenil");
        }
        return all;
    }, [matches, isLeague, selJornada]);

    let timelineMatches = matches;
    let courts, tStart, tEnd, tDur;

    if (isLeague) {
        if (!selJornada) {
            return (
                <div className="vp-card p-10 text-center text-slate-500">
                    Aún no hay jornadas. Ve a "Organizar" y crea una.
                </div>
            );
        }
        timelineMatches = matches.filter((m) => m.jornada_id === selJornada.id);
        courts = selJornada.canchas;
        tStart = selJornada.hora_inicio;
        tEnd = selJornada.hora_fin;
        tDur = tournament.duracion_bloque;
    } else {
        // Filtra por el día seleccionado (día null se considera día 1 legado)
        timelineMatches = matches.filter((m) => (m.dia || 1) === selectedDay);
        courts = tournament.canchas;
        const cfg = (tournament.dias_cfg || [])[selectedDay - 1];
        tStart = (cfg && cfg.hora_inicio) || tournament.hora_inicio;
        tEnd =
            (cfg && cfg.hora_fin) ||
            tournament.hora_fin ||
            (() => {
                const [h, mm] = (tournament.hora_inicio || "09:00")
                    .split(":")
                    .map(Number);
                const endH = (h + 10) % 24;
                return `${String(endH).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
            })();
        tDur = tournament.duracion_bloque;
    }

    return (
        <div className="space-y-6">
            <div className="vp-card p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <h3 className="font-display text-lg font-bold">
                        Cambiar canchas
                    </h3>
                    <p className="text-sm text-slate-500">
                        Reagenda solo los partidos no jugados.
                    </p>
                    <div className="mt-3 flex gap-2">
                        <Input
                            type="number"
                            min={1}
                            value={nuevasCanchas}
                            onChange={(e) =>
                                setNuevasCanchas(parseInt(e.target.value) || 1)
                            }
                            className="h-11 rounded-xl w-24"
                            data-testid="recalc-courts"
                        />
                        <Button
                            onClick={() => onRecalcular(nuevasCanchas)}
                            className="h-11 rounded-xl bg-ipn-800 hover:bg-ipn-900 font-bold"
                            data-testid="recalc-submit"
                        >
                            <RefreshCcw className="w-4 h-4 mr-2" />
                            Recalcular
                        </Button>
                    </div>
                </div>

                {isLeague && (
                    <div>
                        <h3 className="font-display text-lg font-bold">
                            Jornada en edición
                        </h3>
                        <p className="text-sm text-slate-500">
                            Selecciona qué jornada quieres reorganizar.
                        </p>
                        <Select
                            value={selJornada.id}
                            onValueChange={setActiveJornada}
                        >
                            <SelectTrigger
                                className="h-11 rounded-xl mt-3"
                                data-testid="edicion-jornada-select"
                            >
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {jornadas.map((j) => (
                                    <SelectItem key={j.id} value={j.id}>
                                        {j.fecha} · {j.modo}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                )}

                {!isLeague && availableDays.length > 1 && (
                    <div>
                        <h3 className="font-display text-lg font-bold">
                            Día del torneo
                        </h3>
                        <p className="text-sm text-slate-500">
                            El torneo abarca {availableDays.length} días.
                            Selecciona el día a organizar.
                        </p>
                        <div
                            className="mt-3 flex flex-wrap gap-2"
                            data-testid="edicion-day-tabs"
                        >
                            {availableDays.map((d) => (
                                <button
                                    key={d}
                                    type="button"
                                    onClick={() => setSelectedDay(d)}
                                    className={`h-11 px-4 rounded-xl font-bold text-sm border transition-colors ${
                                        selectedDay === d
                                            ? "bg-ipn-800 border-ipn-800 text-white"
                                            : "bg-white border-slate-200 text-slate-700 hover:border-ipn-400"
                                    }`}
                                    data-testid={`edicion-day-${d}`}
                                >
                                    Día {d}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            <TimelineEditor
                matches={timelineMatches}
                numCourts={courts}
                startTime={tStart}
                endTime={tEnd}
                duration={tDur}
                onMove={(mid, patch) =>
                    onMove(mid, isLeague ? patch : { ...patch, dia: selectedDay })
                }
                onSwap={onSwap}
                onRelease={onRelease}
                onFillSlot={(mid, patch) =>
                    onFillSlot(mid, {
                        ...patch,
                        ...(isLeague
                            ? { jornada_id: selJornada.id }
                            : { dia: selectedDay }),
                    })
                }
                draftMatches={draftMatches}
            />
        </div>
    );
}

function ReagendarTab({ tournament, matches, onReagendar }) {
    const [query, setQuery] = useState("");
    const [jornadaFilter, setJornadaFilter] = useState("all");

    const teamsWithCount = useMemo(() => {
        const out = [];
        tournament.categorias.forEach((c) => {
            (c.teams || []).forEach((t) => {
                out.push({
                    name: t.nombre,
                    institucion: t.institucion,
                    rama: c.rama,
                    categoria: c.nombre,
                    reagendados: t.reagendados || 0,
                });
            });
        });
        return out;
    }, [tournament]);

    const allScheduled = matches.filter(
        (m) => m.status === "scheduled" && m.jornada_id,
    );

    const catMap = useMemo(() => {
        const out = {};
        (tournament.categorias || []).forEach((c) => (out[c.id] = c));
        return out;
    }, [tournament]);

    const jorMap = useMemo(() => {
        const out = {};
        (tournament.jornadas || []).forEach((j) => (out[j.id] = j));
        return out;
    }, [tournament]);

    const scheduled = useMemo(() => {
        const raw = query.trim();
        const norm = (s) =>
            (s || "")
                .normalize("NFD")
                .replace(/[\u0300-\u036f]/g, "")
                .toLowerCase();
        const tokens = norm(raw)
            .split(/\s+vs\s+|\s+-\s+|\s+/)
            .map((t) => t.trim())
            .filter(Boolean);
        return allScheduled.filter((m) => {
            if (jornadaFilter !== "all" && m.jornada_id !== jornadaFilter)
                return false;
            if (tokens.length === 0) return true;
            const cat = catMap[m.category_id];
            const jor = jorMap[m.jornada_id];
            const haystack = norm(
                [
                    m.local,
                    m.visitante,
                    m.inst_local,
                    m.inst_vis,
                    m.hora,
                    `cancha ${m.cancha}`,
                    `c${m.cancha}`,
                    m.branch,
                    m.grupo,
                    cat?.nombre,
                    jor?.fecha,
                    `${m.local} vs ${m.visitante}`,
                    `${m.visitante} vs ${m.local}`,
                ]
                    .filter(Boolean)
                    .join(" "),
            );
            return tokens.every((tok) => haystack.includes(tok));
        });
    }, [allScheduled, query, jornadaFilter, catMap, jorMap]);

    return (
        <div className="space-y-6">
            <div className="vp-card p-6">
                <h3 className="font-display text-lg font-bold">
                    Comodines por equipo (máx 2)
                </h3>
                <p className="text-sm text-slate-500 mb-4">
                    El sistema descuenta automáticamente al reagendar.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {teamsWithCount.map((t, i) => (
                        <div
                            key={i}
                            className="p-4 rounded-xl border border-slate-200 flex items-center justify-between"
                            data-testid={`team-counter-${i}`}
                        >
                            <div className="min-w-0">
                                <div className="font-bold truncate">
                                    {t.name}
                                </div>
                                <div className="text-xs text-slate-500 truncate">
                                    {t.categoria} · {t.rama}
                                </div>
                            </div>
                            <div
                                className={`text-xs font-bold px-2 py-1 rounded-full ${
                                    t.reagendados >= 2
                                        ? "bg-rose-100 text-rose-700"
                                        : t.reagendados === 1
                                          ? "bg-yellow-100 text-yellow-800"
                                          : "bg-emerald-100 text-emerald-700"
                                }`}
                            >
                                {t.reagendados}/2
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="vp-card p-6">
                <div className="flex items-start gap-3 flex-wrap mb-3">
                    <div className="flex-1 min-w-[240px]">
                        <h3 className="font-display text-lg font-bold">
                            Partidos programados
                        </h3>
                        <p className="text-sm text-slate-500">
                            Busca el partido (equipo, hora, cancha, jornada,
                            grupo…). Click "Reagendar" para mover.
                        </p>
                    </div>
                    {(tournament.jornadas || []).length > 0 && (
                        <select
                            value={jornadaFilter}
                            onChange={(e) => setJornadaFilter(e.target.value)}
                            className="h-10 rounded-xl border border-slate-200 px-3 text-sm font-bold bg-white"
                            data-testid="reagendar-jornada-filter"
                        >
                            <option value="all">Todas las jornadas</option>
                            {tournament.jornadas.map((j) => (
                                <option key={j.id} value={j.id}>
                                    {j.fecha} · {j.modo}
                                </option>
                            ))}
                        </select>
                    )}
                </div>

                <Input
                    placeholder="Ej. 'Tepepan vs', '10:00 C2', 'jornada 12 abr', 'grupo A'…"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    className="h-11 rounded-xl mb-4"
                    data-testid="reagendar-search"
                />

                <div className="space-y-2">
                    {scheduled.length === 0 && (
                        <div className="text-sm text-slate-500 text-center py-6">
                            {allScheduled.length === 0
                                ? "No hay partidos programados aún."
                                : "Ningún partido coincide con la búsqueda."}
                        </div>
                    )}
                    {scheduled.map((m) => {
                        const cat = catMap[m.category_id];
                        const jor = jorMap[m.jornada_id];
                        return (
                            <div
                                key={m.id}
                                className="flex items-center gap-3 justify-between p-3 rounded-xl bg-slate-50 border border-slate-100"
                                data-testid={`reagendar-row-${m.id}`}
                            >
                                <div className="text-sm flex-1 min-w-0">
                                    <div className="font-bold truncate">
                                        {m.local} vs {m.visitante}
                                    </div>
                                    <div className="text-xs text-slate-500 truncate">
                                        {m.hora} · Cancha {m.cancha} ·{" "}
                                        {m.branch}
                                        {cat ? ` · ${cat.nombre}` : ""}
                                        {jor ? ` · ${jor.fecha}` : ""}
                                        {m.grupo && m.grupo !== "Único"
                                            ? ` · Grupo ${m.grupo}`
                                            : ""}
                                    </div>
                                </div>
                                <Button
                                    size="sm"
                                    onClick={() => onReagendar(m)}
                                    className="rounded-xl bg-ipn-800 hover:bg-ipn-900"
                                    data-testid={`reagendar-btn-${m.id}`}
                                >
                                    Reagendar
                                </Button>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

function VistaPreviaTab({ tournament, matches, onReload }) {
    const total = matches.length;
    const drafts = matches.filter((m) => m.status === "draft").length;
    const scheduled = matches.filter((m) => m.status === "scheduled").length;
    const played = matches.filter((m) => m.status === "played").length;

    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 vp-card p-6">
                <h3 className="font-display text-lg font-bold mb-4">
                    Configuración del torneo
                </h3>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                    <Pair k="Tipo" v={tournament.kind === "league" ? "Liga" : "Torneo Rápido"} />
                    <Pair k="Voley" v={tournament.tipo_voley} />
                    <Pair k="Canchas globales" v={tournament.canchas} />
                    <Pair k="Hora inicio" v={tournament.hora_inicio} />
                    {tournament.hora_fin && (
                        <Pair k="Hora fin" v={tournament.hora_fin} />
                    )}
                    <Pair
                        k="Duración por partido"
                        v={`${tournament.duracion_bloque} min`}
                    />
                    {tournament.kind === "league" && (
                        <>
                            <Pair
                                k="Días por semana"
                                v={tournament.dias_por_semana}
                            />
                            <Pair k="Días" v={(tournament.dias || []).join(", ")} />
                            <Pair
                                k="Ida y vuelta"
                                v={tournament.ida_y_vuelta ? "Sí" : "No"}
                            />
                            <Pair
                                k="Formato playoff"
                                v={tournament.formato_playoff || "Por decidir"}
                            />
                        </>
                    )}
                </dl>

                <BannerUploader tournament={tournament} onReload={onReload} />

                <h3 className="font-display text-lg font-bold mt-8 mb-4">
                    Categorías
                </h3>
                <div className="space-y-2">
                    {tournament.categorias.map((c) => (
                        <CategoriaPreviewRow
                            key={c.id}
                            tournamentId={tournament.id}
                            cat={c}
                            matches={matches}
                            onSaved={onReload}
                        />
                    ))}
                </div>
            </div>

            <div className="space-y-4">
                <div className="vp-card p-6">
                    <Trophy className="w-6 h-6 text-ipn-800" />
                    <div className="mt-3 text-3xl font-display font-black">
                        {total}
                    </div>
                    <div className="text-sm text-slate-500">
                        partidos en total
                    </div>
                </div>
                <div className="vp-card p-6">
                    <Clock className="w-6 h-6 text-yellow-500" />
                    <div className="mt-3 text-3xl font-display font-black">
                        {drafts}
                    </div>
                    <div className="text-sm text-slate-500">en borrador</div>
                </div>
                <div className="vp-card p-6">
                    <Calendar className="w-6 h-6 text-ipn-800" />
                    <div className="mt-3 text-3xl font-display font-black">
                        {scheduled}
                    </div>
                    <div className="text-sm text-slate-500">programados</div>
                </div>
                <div className="vp-card p-6">
                    <Trophy className="w-6 h-6 text-emerald-500" />
                    <div className="mt-3 text-3xl font-display font-black">
                        {played}
                    </div>
                    <div className="text-sm text-slate-500">jugados</div>
                </div>
            </div>
        </div>
    );
}

function Pair({ k, v }) {
    return (
        <div>
            <dt className="text-xs uppercase font-bold tracking-widest text-slate-400">
                {k}
            </dt>
            <dd className="mt-1 font-semibold">{v ?? "—"}</dd>
        </div>
    );
}

function CategoriaPreviewRow({ tournamentId, cat, matches, onSaved }) {
    const [addOpen, setAddOpen] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const [editTeam, setEditTeam] = useState(null);

    const teamsByGroup = useMemo(() => {
        const teamGrupo = {};
        (matches || [])
            .filter((m) => m.category_id === cat.id)
            .forEach((m) => {
                const g = m.grupo || "Único";
                if (m.local && !teamGrupo[m.local]) teamGrupo[m.local] = g;
                if (m.visitante && !teamGrupo[m.visitante])
                    teamGrupo[m.visitante] = g;
            });
        const map = {};
        (cat.teams || []).forEach((t) => {
            const g = teamGrupo[t.nombre] || "-";
            map[g] = map[g] || [];
            map[g].push(t);
        });
        return map;
    }, [cat, matches]);

    const groupsSorted = Object.keys(teamsByGroup).sort();

    const doDelete = async (team) => {
        if (
            !window.confirm(
                `¿Eliminar "${team.nombre}" de ${cat.nombre}? Se borrarán TODOS sus partidos (jugados y pendientes) para que no afecten las estadísticas.`,
            )
        )
            return;
        try {
            const { data } = await api.delete(
                `/tournaments/${tournamentId}/categories/${cat.id}/teams/${encodeURIComponent(team.nombre)}`,
            );
            toast.success(
                `Equipo eliminado. Se borraron ${data.matches_deleted} partidos.`,
            );
            onSaved?.();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        }
    };

    return (
        <>
            <div className="rounded-xl bg-slate-50 border border-slate-100 overflow-hidden">
                <div className="p-4 flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2 min-w-0">
                        <span
                            className={`text-xs font-bold uppercase px-2 py-0.5 rounded-full ${
                                cat.rama === "femenil"
                                    ? "bg-rose-100 text-rose-700"
                                    : "bg-ipn-100 text-ipn-900"
                            }`}
                        >
                            {cat.rama}
                        </span>
                        <span className="font-bold truncate">{cat.nombre}</span>
                    </div>
                    <div className="flex items-center gap-3">
                        <span className="text-sm text-slate-500">
                            {cat.teams?.length || 0} equipos · {cat.formato}
                        </span>
                        <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setExpanded((x) => !x)}
                            className="rounded-xl"
                            data-testid={`toggle-teams-${cat.id}`}
                        >
                            {expanded ? "Ocultar" : "Ver equipos"}
                        </Button>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setAddOpen(true)}
                            className="rounded-xl"
                            data-testid={`add-team-open-${cat.id}`}
                        >
                            <Plus className="w-3 h-3 mr-1" />
                            Agregar equipo
                        </Button>
                    </div>
                </div>
                {expanded && (
                    <div className="border-t border-slate-100 bg-white p-4 space-y-3">
                        {groupsSorted.length === 0 && (
                            <div className="text-sm text-slate-500 italic">
                                Sin equipos aún.
                            </div>
                        )}
                        {groupsSorted.map((g) => (
                            <div key={g}>
                                {g !== "-" && g !== "Único" && (
                                    <div className="text-[10px] uppercase font-bold tracking-widest text-ipn-800 mb-1.5">
                                        Grupo {g}
                                    </div>
                                )}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                    {teamsByGroup[g].map((t) => (
                                        <div
                                            key={t.nombre}
                                            className="p-2.5 rounded-lg border border-slate-100 flex items-center justify-between gap-2"
                                            data-testid={`team-item-${cat.id}-${t.nombre}`}
                                        >
                                            <div className="flex items-center gap-2 min-w-0">
                                                {t.logo_url && (
                                                    <img
                                                        src={t.logo_url}
                                                        alt=""
                                                        className="w-8 h-8 rounded-full object-cover border border-slate-200 shrink-0"
                                                    />
                                                )}
                                                <div className="min-w-0">
                                                    <div className="font-bold text-sm truncate">
                                                        {t.nombre}
                                                    </div>
                                                    {t.institucion && (
                                                        <div className="text-xs text-slate-500 truncate">
                                                            {t.institucion}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-1 flex-shrink-0">
                                                <button
                                                    type="button"
                                                    onClick={() => setEditTeam(t)}
                                                    className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-600"
                                                    title="Editar"
                                                    data-testid={`edit-team-${cat.id}-${t.nombre}`}
                                                >
                                                    <Pencil className="w-4 h-4" />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => doDelete(t)}
                                                    className="p-1.5 rounded-lg hover:bg-rose-50 text-rose-600"
                                                    title="Eliminar"
                                                    data-testid={`delete-team-${cat.id}-${t.nombre}`}
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            {addOpen && (
                <AddTeamDialog
                    tournamentId={tournamentId}
                    categoria={cat}
                    matches={matches}
                    open={addOpen}
                    onOpenChange={setAddOpen}
                    onSaved={onSaved}
                />
            )}
            {editTeam && (
                <EditTeamDialog
                    tournamentId={tournamentId}
                    categoria={cat}
                    team={editTeam}
                    open={!!editTeam}
                    onOpenChange={(o) => !o && setEditTeam(null)}
                    onSaved={() => {
                        setEditTeam(null);
                        onSaved?.();
                    }}
                />
            )}
        </>
    );
}

function BannerUploader({ tournament, onReload }) {
    const [uploading, setUploading] = useState(false);
    const inputRef = useRef(null);

    const fullUrl = (u) => {
        if (!u) return "";
        if (u.startsWith("http")) return u;
        return `${process.env.REACT_APP_BACKEND_URL}${u}`;
    };

    const onFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const form = new FormData();
            form.append("file", file);
            const { data } = await api.post("/uploads/image", form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            await api.patch(`/tournaments/${tournament.id}/settings`, {
                banner_url: data.url,
            });
            toast.success("Banner actualizado");
            onReload?.();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setUploading(false);
            if (inputRef.current) inputRef.current.value = "";
        }
    };

    const removeBanner = async () => {
        if (!window.confirm("¿Quitar imagen de fondo?")) return;
        try {
            await api.patch(`/tournaments/${tournament.id}/settings`, {
                banner_url: "",
            });
            toast.success("Banner removido");
            onReload?.();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        }
    };

    return (
        <div className="mt-6 p-4 rounded-xl border-2 border-dashed border-slate-200">
            <div className="flex items-start gap-3 flex-wrap">
                <div className="w-32 h-20 rounded-lg overflow-hidden bg-gradient-to-br from-ipn-900 to-ipn-950 flex items-center justify-center shrink-0">
                    {tournament.banner_url ? (
                        <img
                            src={fullUrl(tournament.banner_url)}
                            alt="Banner"
                            className="w-full h-full object-cover"
                        />
                    ) : (
                        <ImageIcon className="w-6 h-6 text-white/50" />
                    )}
                </div>
                <div className="flex-1 min-w-[200px]">
                    <div className="text-xs uppercase font-bold tracking-widest text-slate-500">
                        Imagen de fondo del banner público
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                        JPG/PNG/WEBP hasta 2MB. Se aplica un overlay guinda
                        automáticamente para mantener legibles los textos.
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                        <input
                            ref={inputRef}
                            type="file"
                            accept="image/*"
                            onChange={onFile}
                            className="hidden"
                            data-testid="banner-file-input"
                        />
                        <Button
                            type="button"
                            size="sm"
                            onClick={() => inputRef.current?.click()}
                            disabled={uploading}
                            className="rounded-xl bg-ipn-800 hover:bg-ipn-900"
                            data-testid="banner-upload-btn"
                        >
                            {uploading
                                ? "Subiendo…"
                                : tournament.banner_url
                                  ? "Cambiar imagen"
                                  : "Subir imagen"}
                        </Button>
                        {tournament.banner_url && (
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={removeBanner}
                                className="rounded-xl text-rose-600 hover:bg-rose-50"
                                data-testid="banner-remove-btn"
                            >
                                Quitar
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function EditTeamDialog({ tournamentId, categoria, team, open, onOpenChange, onSaved }) {
    const [nombre, setNombre] = useState(team.nombre);
    const [institucion, setInstitucion] = useState(team.institucion || "");
    const [logoUrl, setLogoUrl] = useState(team.logo_url || "");
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [saving, setSaving] = useState(false);

    const uploadLogo = async (file) => {
        if (!file) return;
        setUploadingLogo(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/uploads/image", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setLogoUrl(data.url);
            toast.success("Escudo cargado");
        } catch (err) {
            toast.error(
                formatApiError(err.response?.data?.detail) ||
                    "No se pudo subir el escudo",
            );
        } finally {
            setUploadingLogo(false);
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!nombre.trim()) return toast.error("Pon el nombre del equipo");
        setSaving(true);
        try {
            await api.patch(
                `/tournaments/${tournamentId}/categories/${categoria.id}/teams/${encodeURIComponent(
                    team.nombre,
                )}`,
                {
                    nombre: nombre.trim(),
                    institucion: institucion.trim(),
                    logo_url: logoUrl || "",
                },
            );
            toast.success("Equipo actualizado");
            onSaved?.();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-2xl rounded-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="font-display">
                        Editar equipo · {team.nombre}
                    </DialogTitle>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-4 mt-2">
                    <div className="grid sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label>Nombre</Label>
                            <Input
                                value={nombre}
                                onChange={(e) => setNombre(e.target.value)}
                                className="h-11 rounded-xl"
                                data-testid="edit-team-name"
                                autoFocus
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Institución</Label>
                            <Input
                                value={institucion}
                                onChange={(e) => setInstitucion(e.target.value)}
                                className="h-11 rounded-xl"
                                data-testid="edit-team-inst"
                            />
                        </div>
                    </div>
                    <div className="space-y-2">
                        <Label>Escudo del equipo</Label>
                        <div className="flex items-center gap-3">
                            <div className="w-14 h-14 rounded-xl border-2 border-dashed border-slate-200 flex items-center justify-center overflow-hidden bg-slate-50">
                                {logoUrl ? (
                                    <img
                                        src={logoUrl}
                                        alt="escudo"
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <span className="text-[10px] text-slate-400">
                                        sin escudo
                                    </span>
                                )}
                            </div>
                            <div className="flex-1 space-y-1">
                                <input
                                    id="edit-team-logo-input"
                                    type="file"
                                    accept="image/png,image/jpeg,image/webp,image/gif"
                                    className="hidden"
                                    data-testid="edit-team-logo-input"
                                    onChange={(e) =>
                                        uploadLogo(e.target.files?.[0])
                                    }
                                />
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    disabled={uploadingLogo}
                                    onClick={() =>
                                        document
                                            .getElementById(
                                                "edit-team-logo-input",
                                            )
                                            ?.click()
                                    }
                                    className="h-9 rounded-xl text-xs"
                                    data-testid="edit-team-logo-upload"
                                >
                                    {uploadingLogo
                                        ? "Subiendo…"
                                        : logoUrl
                                          ? "Cambiar escudo"
                                          : "Subir escudo"}
                                </Button>
                                {logoUrl && (
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => setLogoUrl("")}
                                        className="h-7 rounded-xl text-xs text-rose-600 hover:text-rose-800"
                                        data-testid="edit-team-logo-clear"
                                    >
                                        Quitar
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>

                    <RosterEditor
                        tournamentId={tournamentId}
                        categoria={categoria}
                        team={team}
                        onSaved={onSaved}
                    />
                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            className="rounded-xl"
                        >
                            Cancelar
                        </Button>
                        <Button
                            type="submit"
                            disabled={saving}
                            className="rounded-xl bg-ipn-800 hover:bg-ipn-900"
                            data-testid="edit-team-submit"
                        >
                            {saving ? "Guardando…" : "Guardar"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function RefereePinCard({ tournamentId, tournament }) {
    const [pin, setPin] = useState(null);
    const [show, setShow] = useState(false);
    const [open, setOpen] = useState(false);
    const slug = tournament?.slug || "";
    const refereeUrl = `${window.location.origin}/arbitro?code=${slug}`;

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(
                    `/tournaments/${tournamentId}/referee-pin`,
                );
                setPin(data.pin);
            } catch {}
        })();
    }, [tournamentId]);

    const rotate = async () => {
        try {
            const { data } = await api.post(
                `/tournaments/${tournamentId}/referee-pin`,
            );
            setPin(data.pin);
            toast.success("PIN regenerado");
        } catch {
            toast.error("No se pudo regenerar el PIN");
        }
    };

    const copyPin = () => {
        if (!pin) return;
        navigator.clipboard?.writeText(pin);
        toast.success("PIN copiado");
    };

    const copyCode = () => {
        navigator.clipboard?.writeText(slug);
        toast.success("Código copiado");
    };

    const shareAll = () => {
        if (!pin) return;
        const text = `🏐 Acceso de árbitro · ${tournament?.name || "Torneo"}

Entra aquí: ${refereeUrl}

Código del torneo: ${slug}
PIN: ${pin}`;
        navigator.clipboard?.writeText(text);
        toast.success("Instrucciones copiadas — pégalas en WhatsApp");
    };

    return (
        <div className="relative">
            <button
                onClick={() => setOpen(!open)}
                className="inline-flex items-center gap-2 h-11 px-4 rounded-xl bg-ipnYellow hover:bg-ipnYellow-600 text-ipn-950 font-bold text-sm"
                data-testid="open-referee-pin"
            >
                <Flag className="w-4 h-4" />
                PIN árbitros
            </button>
            {open && (
                <div className="absolute right-0 mt-2 w-96 vp-card p-4 z-50 shadow-lg">
                    <p className="text-xs font-bold uppercase tracking-widest text-slate-500">
                        Acceso de árbitros
                    </p>

                    {/* Código del torneo */}
                    <div className="mt-3">
                        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">
                            Código del torneo
                        </div>
                        <div className="flex items-center gap-2">
                            <div
                                className="flex-1 font-mono text-sm font-bold bg-slate-50 border border-slate-100 rounded-xl px-3 py-2 truncate"
                                data-testid="referee-code-value"
                                title={slug}
                            >
                                {slug}
                            </div>
                            <button
                                onClick={copyCode}
                                className="h-10 w-10 rounded-xl border border-slate-200 grid place-items-center hover:bg-slate-50"
                                title="Copiar código"
                                data-testid="copy-code"
                            >
                                <Copy className="w-4 h-4" />
                            </button>
                        </div>
                    </div>

                    {/* PIN */}
                    <div className="mt-4">
                        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">
                            PIN (4 dígitos)
                        </div>
                        <div className="flex items-center gap-2">
                            <div
                                className="flex-1 text-center font-mono text-3xl font-black bg-ipn-50 rounded-xl py-3 tracking-[0.3em] text-ipn-900"
                                data-testid="referee-pin-value"
                            >
                                {show ? pin || "…" : "••••"}
                            </div>
                            <button
                                onClick={() => setShow(!show)}
                                className="h-11 w-11 rounded-xl border border-slate-200 grid place-items-center hover:bg-slate-50"
                                title={show ? "Ocultar" : "Mostrar"}
                                data-testid="toggle-pin-visibility"
                            >
                                {show ? (
                                    <EyeOff className="w-4 h-4" />
                                ) : (
                                    <EyeIcon className="w-4 h-4" />
                                )}
                            </button>
                            <button
                                onClick={copyPin}
                                className="h-11 w-11 rounded-xl border border-slate-200 grid place-items-center hover:bg-slate-50"
                                title="Copiar PIN"
                                data-testid="copy-pin"
                            >
                                <Copy className="w-4 h-4" />
                            </button>
                        </div>
                    </div>

                    {/* Acciones */}
                    <div className="mt-4 flex gap-2">
                        <Button
                            size="sm"
                            onClick={shareAll}
                            className="rounded-xl flex-1 bg-ipn-800 hover:bg-ipn-900 font-bold"
                            data-testid="share-access"
                        >
                            <Copy className="w-3 h-3 mr-1" />
                            Compartir acceso
                        </Button>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={rotate}
                            className="rounded-xl"
                            data-testid="rotate-pin"
                            title="Regenerar PIN"
                        >
                            <RefreshCcw className="w-3 h-3" />
                        </Button>
                    </div>

                    <p className="text-xs text-slate-500 mt-3 leading-relaxed">
                        Los árbitros entran en <code className="font-mono text-ipn-800">/arbitro</code> y meten{" "}
                        <strong>código</strong> + <strong>PIN</strong>. Si
                        regeneras el PIN, el anterior deja de funcionar.
                    </p>
                </div>
            )}
        </div>
    );
}


function ReglamentoEditor({ tournament, onSaved }) {
    const [text, setText] = useState(tournament.reglamento || "");
    const [saving, setSaving] = useState(false);
    const dirty = text !== (tournament.reglamento || "");

    const save = async () => {
        setSaving(true);
        try {
            await api.patch(`/tournaments/${tournament.id}/reglamento`, {
                reglamento: text,
            });
            toast.success("Reglamento guardado");
            onSaved?.();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="vp-card p-6 space-y-4">
            <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                    <h3 className="font-display text-xl font-black">
                        Reglamento y convocatoria
                    </h3>
                    <p className="text-sm text-slate-500 mt-1 max-w-2xl">
                        Redacta las bases oficiales del torneo. El público podrá
                        consultarlo desde la pestaña{" "}
                        <strong>Reglamento</strong> de la vista pública.
                        Soporta saltos de línea; usa <code>###</code> para
                        encabezados simples.
                    </p>
                </div>
                <Button
                    type="button"
                    disabled={!dirty || saving}
                    onClick={save}
                    className="rounded-xl bg-ipn-800 hover:bg-ipn-900"
                    data-testid="reglamento-save"
                >
                    {saving ? "Guardando…" : "Guardar reglamento"}
                </Button>
            </div>
            <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={18}
                placeholder="### Convocatoria oficial&#10;&#10;1. Elegibilidad&#10;Los equipos deberán…&#10;&#10;### Formato de competencia&#10;…"
                className="w-full rounded-xl border border-slate-200 p-4 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-ipn-400"
                data-testid="reglamento-textarea"
            />
            <p className="text-[11px] text-slate-400">
                {text.length.toLocaleString()} caracteres
            </p>
        </div>
    );
}



function FriendlyMatchDialog({ tournament, open, onOpenChange, onSaved }) {
    const [categoryId, setCategoryId] = useState(
        tournament.categorias?.[0]?.id || "",
    );
    const [local, setLocal] = useState("");
    const [visitante, setVisitante] = useState("");
    const [hora, setHora] = useState("");
    const [cancha, setCancha] = useState(1);
    const [saving, setSaving] = useState(false);

    const cat = tournament.categorias?.find((c) => c.id === categoryId);
    const teamOptions = cat?.teams || [];

    const submit = async (e) => {
        e.preventDefault();
        if (!local.trim() || !visitante.trim())
            return toast.error("Selecciona ambos equipos");
        if (local.trim() === visitante.trim())
            return toast.error("Los equipos deben ser diferentes");
        setSaving(true);
        try {
            await api.post(`/tournaments/${tournament.id}/friendly-match`, {
                category_id: categoryId,
                local: local.trim(),
                visitante: visitante.trim(),
                hora: hora.trim() || undefined,
                cancha: Number(cancha) || 1,
                dia: 1,
            });
            toast.success("Partido amistoso creado");
            onSaved?.();
            onOpenChange(false);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-md rounded-2xl">
                <DialogHeader>
                    <DialogTitle className="font-display">
                        Nuevo partido amistoso
                    </DialogTitle>
                    <DialogDescription>
                        Los partidos amistosos no suman a la tabla ni a las
                        estadísticas del torneo.
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-3 mt-2">
                    <div className="space-y-1">
                        <Label className="text-xs">Categoría</Label>
                        <select
                            value={categoryId}
                            onChange={(e) => {
                                setCategoryId(e.target.value);
                                setLocal("");
                                setVisitante("");
                            }}
                            className="w-full h-11 rounded-xl border border-slate-200 px-3 text-sm"
                            data-testid="friendly-category"
                        >
                            {tournament.categorias?.map((c) => (
                                <option key={c.id} value={c.id}>
                                    {c.nombre} · {c.rama}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                            <Label className="text-xs">Equipo local</Label>
                            <select
                                value={local}
                                onChange={(e) => setLocal(e.target.value)}
                                className="w-full h-11 rounded-xl border border-slate-200 px-3 text-sm"
                                data-testid="friendly-local"
                            >
                                <option value="">—</option>
                                {teamOptions.map((t) => (
                                    <option key={t.nombre} value={t.nombre}>
                                        {t.nombre}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="space-y-1">
                            <Label className="text-xs">Equipo visitante</Label>
                            <select
                                value={visitante}
                                onChange={(e) => setVisitante(e.target.value)}
                                className="w-full h-11 rounded-xl border border-slate-200 px-3 text-sm"
                                data-testid="friendly-vis"
                            >
                                <option value="">—</option>
                                {teamOptions.map((t) => (
                                    <option key={t.nombre} value={t.nombre}>
                                        {t.nombre}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                            <Label className="text-xs">Hora (opcional)</Label>
                            <Input
                                value={hora}
                                onChange={(e) => setHora(e.target.value)}
                                placeholder="HH:MM"
                                className="h-11 rounded-xl"
                                data-testid="friendly-hora"
                            />
                        </div>
                        <div className="space-y-1">
                            <Label className="text-xs">Cancha</Label>
                            <Input
                                type="number"
                                min={1}
                                value={cancha}
                                onChange={(e) => setCancha(e.target.value)}
                                className="h-11 rounded-xl"
                                data-testid="friendly-cancha"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            className="rounded-xl"
                        >
                            Cancelar
                        </Button>
                        <Button
                            type="submit"
                            disabled={saving}
                            className="rounded-xl bg-amber-600 hover:bg-amber-700"
                            data-testid="friendly-submit"
                        >
                            {saving ? "Creando…" : "Crear amistoso"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function RosterEditor({ tournamentId, categoria, team, onSaved }) {
    const [players, setPlayers] = useState(team.players || []);
    const [adding, setAdding] = useState(false);
    const [editing, setEditing] = useState(null);

    const refresh = (next) => {
        setPlayers(next);
        onSaved?.();
    };

    const openAdd = () => {
        setEditing({ id: null, nombre: "", matricula: "", foto_url: "" });
        setAdding(true);
    };

    const openEdit = (p) => {
        setEditing({ ...p });
        setAdding(true);
    };

    const removePlayer = async (p) => {
        if (!window.confirm(`¿Eliminar a ${p.nombre} del roster?`)) return;
        try {
            await api.delete(
                `/tournaments/${tournamentId}/categories/${categoria.id}/teams/${encodeURIComponent(team.nombre)}/players/${p.id}`,
            );
            refresh(players.filter((x) => x.id !== p.id));
            toast.success("Jugador eliminado");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        }
    };

    return (
        <div className="rounded-xl border border-slate-200 p-4 space-y-3 bg-slate-50/60">
            <div className="flex items-center justify-between">
                <div>
                    <h4 className="font-display font-bold text-sm flex items-center gap-2">
                        <Users className="w-4 h-4 text-ipn-800" />
                        Roster de jugadores
                        <span className="text-xs font-normal text-slate-500">
                            ({players.length})
                        </span>
                    </h4>
                    <p className="text-[11px] text-slate-500">
                        Nombre, matrícula y foto. El público solo lo ve en modo
                        lectura.
                    </p>
                </div>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={openAdd}
                    className="h-9 rounded-xl"
                    data-testid="roster-add"
                >
                    <PlusIcon className="w-4 h-4 mr-1" />
                    Añadir jugador
                </Button>
            </div>

            {players.length === 0 ? (
                <div className="text-center text-xs text-slate-500 py-3">
                    Aún no hay jugadores en el roster.
                </div>
            ) : (
                <div className="grid sm:grid-cols-2 gap-2">
                    {players.map((p) => (
                        <div
                            key={p.id}
                            className="flex items-center gap-3 p-2 rounded-lg bg-white border border-slate-200"
                            data-testid={`roster-row-${p.id}`}
                        >
                            <div className="w-10 h-10 rounded-full overflow-hidden bg-slate-100 border border-slate-200 shrink-0">
                                {p.foto_url ? (
                                    <img
                                        src={p.foto_url}
                                        alt=""
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-slate-300">
                                        <Users className="w-5 h-5" />
                                    </div>
                                )}
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="font-bold text-sm truncate">
                                    {p.nombre}
                                </div>
                                {p.matricula && (
                                    <div className="text-[10px] text-slate-500 font-mono">
                                        Mat. {p.matricula}
                                    </div>
                                )}
                            </div>
                            <button
                                type="button"
                                onClick={() => openEdit(p)}
                                className="p-1.5 rounded hover:bg-slate-100 text-slate-600"
                                title="Editar"
                                data-testid={`roster-edit-${p.id}`}
                            >
                                <Pencil className="w-3.5 h-3.5" />
                            </button>
                            <button
                                type="button"
                                onClick={() => removePlayer(p)}
                                className="p-1.5 rounded hover:bg-rose-50 text-rose-600"
                                title="Eliminar"
                                data-testid={`roster-delete-${p.id}`}
                            >
                                <Trash2 className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {adding && (
                <PlayerDialog
                    tournamentId={tournamentId}
                    categoriaId={categoria.id}
                    teamName={team.nombre}
                    player={editing}
                    open={adding}
                    onOpenChange={setAdding}
                    onSaved={(player, isNew) => {
                        if (isNew) refresh([...players, player]);
                        else
                            refresh(
                                players.map((x) =>
                                    x.id === player.id ? player : x,
                                ),
                            );
                    }}
                />
            )}
        </div>
    );
}

function PlayerDialog({
    tournamentId,
    categoriaId,
    teamName,
    player,
    open,
    onOpenChange,
    onSaved,
}) {
    const isNew = !player.id;
    const [nombre, setNombre] = useState(player.nombre || "");
    const [matricula, setMatricula] = useState(player.matricula || "");
    const [fotoUrl, setFotoUrl] = useState(player.foto_url || "");
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);

    const uploadFoto = async (file) => {
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/uploads/image", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setFotoUrl(data.url);
            toast.success("Foto cargada");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setUploading(false);
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!nombre.trim()) return toast.error("Pon el nombre del jugador");
        setSaving(true);
        try {
            const base = `/tournaments/${tournamentId}/categories/${categoriaId}/teams/${encodeURIComponent(teamName)}/players`;
            const payload = {
                nombre: nombre.trim(),
                matricula: matricula.trim(),
                foto_url: fotoUrl || "",
            };
            const { data } = isNew
                ? await api.post(base, payload)
                : await api.patch(`${base}/${player.id}`, payload);
            toast.success(isNew ? "Jugador añadido" : "Jugador actualizado");
            onSaved(data.player, isNew);
            onOpenChange(false);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-sm rounded-2xl">
                <DialogHeader>
                    <DialogTitle className="font-display">
                        {isNew ? "Nuevo jugador" : "Editar jugador"}
                    </DialogTitle>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-3 mt-2">
                    <div className="space-y-1">
                        <Label className="text-xs">Nombre completo</Label>
                        <Input
                            value={nombre}
                            onChange={(e) => setNombre(e.target.value)}
                            className="h-11 rounded-xl"
                            data-testid="player-name"
                            autoFocus
                        />
                    </div>
                    <div className="space-y-1">
                        <Label className="text-xs">Matrícula</Label>
                        <Input
                            value={matricula}
                            onChange={(e) => setMatricula(e.target.value)}
                            className="h-11 rounded-xl font-mono"
                            data-testid="player-matricula"
                            placeholder="2024320123"
                        />
                    </div>
                    <div className="space-y-1">
                        <Label className="text-xs">Foto</Label>
                        <div className="flex items-center gap-3">
                            <div className="w-14 h-14 rounded-full overflow-hidden bg-slate-100 border border-slate-200 shrink-0">
                                {fotoUrl ? (
                                    <img
                                        src={fotoUrl}
                                        alt=""
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-slate-300">
                                        <Users className="w-6 h-6" />
                                    </div>
                                )}
                            </div>
                            <div>
                                <input
                                    id="player-foto-input"
                                    type="file"
                                    accept="image/png,image/jpeg,image/webp,image/gif"
                                    className="hidden"
                                    data-testid="player-foto-input"
                                    onChange={(e) =>
                                        uploadFoto(e.target.files?.[0])
                                    }
                                />
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    disabled={uploading}
                                    onClick={() =>
                                        document
                                            .getElementById(
                                                "player-foto-input",
                                            )
                                            ?.click()
                                    }
                                    className="h-9 rounded-xl text-xs"
                                    data-testid="player-foto-upload"
                                >
                                    {uploading
                                        ? "Subiendo…"
                                        : fotoUrl
                                          ? "Cambiar foto"
                                          : "Subir foto"}
                                </Button>
                                {fotoUrl && (
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => setFotoUrl("")}
                                        className="h-7 rounded-xl text-xs text-rose-600"
                                    >
                                        Quitar
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            className="rounded-xl"
                        >
                            Cancelar
                        </Button>
                        <Button
                            type="submit"
                            disabled={saving}
                            className="rounded-xl bg-ipn-800 hover:bg-ipn-900"
                            data-testid="player-submit"
                        >
                            {saving ? "Guardando…" : isNew ? "Añadir" : "Guardar"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

